import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Modal } from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { COMPANY_ROLES, ROLE_DESC, ROLE_LABEL, ROLES, Role } from '../lib/roles';

interface TargetCompanyInfo {
  id: string;
  name: string;
}

interface UserRow {
  id: string;
  name: string;
  email: string;
  role: Role;
  active: boolean;
  createdAt: string;
  invitedByUserId?: string | null;
}

interface InviteResponse extends UserRow {
  tempPassword: string;
  inviteMessage: string;
}

const ROLE_BADGE: Record<Role, string> = {
  SUPER_ADMIN: 'b-teal',
  ADMIN: 'b-teal',
  MANAGER: 'b-amber',
  OPERADOR: 'b-gray',
  MOTORISTA: 'b-gray'
};

export default function Users() {
  const { me, login } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetCompanyId = searchParams.get('companyId');
  const isSuperAdminContext = me?.role === ROLES.SUPER_ADMIN;
  // Query axios adicional: SUPER_ADMIN precisa mandar ?companyId=X para o
  // backend saber em qual empresa agir. ADMIN da empresa não passa nada.
  // useMemo: sem isso, o objeto seria recriado a cada render, o useCallback
  // dependente iria invalidar, e o useEffect entraria em loop infinito.
  const scopeParams = useMemo(
    () => (isSuperAdminContext && targetCompanyId ? { companyId: targetCompanyId } : undefined),
    [isSuperAdminContext, targetCompanyId]
  );

  const [targetCompany, setTargetCompany] = useState<TargetCompanyInfo | null>(null);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState<Role | ''>('');
  const [filterActive, setFilterActive] = useState<'todos' | 'ativos' | 'inativos'>('todos');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>(ROLES.OPERADOR);
  const [active, setActive] = useState(true);

  const [inviteResult, setInviteResult] = useState<InviteResponse | null>(null);
  const [resetResult, setResetResult] = useState<{ email: string; tempPassword: string | null } | null>(null);
  // Estado dos diálogos de confirmação
  const [resetTarget, setResetTarget] = useState<UserRow | null>(null);
  const [resetMode, setResetMode] = useState<'random' | 'manual'>('random');
  const [resetManualPassword, setResetManualPassword] = useState('');
  const [resetShowPw, setResetShowPw] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<UserRow | null>(null);
  const [confirmImpersonate, setConfirmImpersonate] = useState<UserRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<UserRow[]>('/users', { params: scopeParams });
      setUsers(data);
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Não foi possível carregar usuários', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, scopeParams]);

  // Quando SUPER_ADMIN entra com ?companyId=X, também busca o nome
  // dessa empresa para mostrar no cabeçalho ("Usuários — Express ABC").
  useEffect(() => {
    if (!me) return;
    if (isSuperAdminContext && targetCompanyId) {
      api.get<Array<{ id: string; name: string }>>('/admin/companies')
        .then(({ data }) => {
          const found = data.find((c) => c.id === targetCompanyId);
          setTargetCompany(found ? { id: found.id, name: found.name } : null);
        })
        .catch(() => setTargetCompany(null));
    }
    if (!isSuperAdminContext || targetCompanyId) {
      load();
    }
  }, [me, isSuperAdminContext, targetCompanyId, load]);

  // SUPER_ADMIN sem ?companyId= não tem escopo: manda pro painel escolher.
  if (me && isSuperAdminContext && !targetCompanyId) {
    return <Navigate to="/admin" replace />;
  }

  const openCreate = () => {
    setEditing(null);
    setName('');
    setEmail('');
    setRole(ROLES.OPERADOR);
    setActive(true);
    setModalOpen(true);
  };

  const openEdit = (u: UserRow) => {
    setEditing(u);
    setName(u.name);
    setEmail(u.email);
    setRole(u.role);
    setActive(u.active);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim() || !email.trim()) {
      showToast('Informe nome e email.', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          role,
          active
        }, { params: scopeParams });
        showToast('Usuário atualizado.', 'success');
        setModalOpen(false);
        await load();
      } else {
        const { data } = await api.post<InviteResponse>('/users/invite', {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          role
        }, { params: scopeParams });
        setInviteResult(data);
        setModalOpen(false);
        await load();
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Não foi possível salvar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const openResetPassword = (u: UserRow) => {
    setResetTarget(u);
    setResetMode('random');
    setResetManualPassword('');
    setResetShowPw(false);
    setResetError('');
  };

  const closeResetDialog = () => {
    setResetTarget(null);
    setResetManualPassword('');
    setResetError('');
    setResetSubmitting(false);
  };

  const submitResetPassword = async () => {
    if (!resetTarget) return;
    setResetError('');

    let body: { password?: string } | undefined;
    if (resetMode === 'manual') {
      const value = resetManualPassword.trim();
      if (value.length < 8) {
        setResetError('Senha muito curta. Use no mínimo 8 caracteres.');
        return;
      }
      body = { password: value };
    }

    setResetSubmitting(true);
    try {
      const { data } = await api.post<{ tempPassword: string | null; generated: boolean }>(
        `/users/${resetTarget.id}/reset-password`,
        body,
        { params: scopeParams }
      );
      const target = resetTarget;
      closeResetDialog();
      if (data.generated && data.tempPassword) {
        setResetResult({ email: target.email, tempPassword: data.tempPassword });
      } else {
        showToast(`Senha de ${target.email} atualizada.`, 'success');
      }
    } catch (err: any) {
      setResetError(err?.response?.data?.error || 'Falha ao resetar senha');
    } finally {
      setResetSubmitting(false);
    }
  };

  const doDelete = async (u: UserRow) => {
    try {
      await api.delete(`/users/${u.id}`, { params: scopeParams });
      showToast('Usuário removido.', 'success');
      await load();
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Falha ao remover', 'error');
    }
  };

  const doImpersonate = async (u: UserRow) => {
    try {
      const { data } = await api.post<{ token: string }>(`/admin/impersonate/${u.id}`);
      login(data.token);
      navigate('/dashboard');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Falha ao impersonar', 'error');
    }
  };

  const copy = (value: string) => {
    navigator.clipboard.writeText(value).then(
      () => showToast('Copiado.', 'success'),
      () => showToast('Não foi possível copiar.', 'error')
    );
  };

  const filtered = users.filter((u) => {
    if (filterRole && u.role !== filterRole) return false;
    if (filterActive === 'ativos' && !u.active) return false;
    if (filterActive === 'inativos' && u.active) return false;
    return true;
  });

  const total = users.length;
  const ativos = users.filter((u) => u.active).length;
  const admins = users.filter((u) => u.role === ROLES.ADMIN && u.active).length;

  return (
    <Layout>
      {isSuperAdminContext && (
        <div style={{ padding: '10px 14px', background: '#7c2d12', color: '#fff', marginBottom: 16, borderRadius: 6, display: 'flex', alignItems: 'center', gap: 12 }}>
          <strong>Modo suporte:</strong>
          <span>gerenciando usuários de <strong>{targetCompany?.name || '…'}</strong> como SUPER_ADMIN.</span>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => navigate('/admin')}
            style={{ marginLeft: 'auto', color: '#fff', background: 'transparent', borderColor: 'rgba(255,255,255,.5)' }}
          >
            Voltar ao Painel do SaaS
          </button>
        </div>
      )}
      <div className="page-head">
        <div>
          <h1>
            {isSuperAdminContext
              ? `Usuários — ${targetCompany?.name || '…'}`
              : 'Usuários da empresa'}
          </h1>
          <p className="sub">Gerencie quem tem acesso e o que cada papel pode fazer.</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          Convidar usuário
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat">
          <div className="lbl">Total</div>
          <div className="val">{total}</div>
        </div>
        <div className="stat">
          <div className="lbl">Ativos</div>
          <div className="val">{ativos}</div>
        </div>
        <div className="stat">
          <div className="lbl">Admins ativos</div>
          <div className="val">{admins}</div>
        </div>
      </div>

      <div className="card">
        <div className="tbl-wrap" style={{ padding: '0 0 8px' }}>
          <div style={{ display: 'flex', gap: 8, padding: '10px 12px', flexWrap: 'wrap' }}>
            <span
              className={`filter-chip ${filterActive === 'todos' ? 'on' : ''}`}
              onClick={() => setFilterActive('todos')}
              role="button"
            >
              Todos
            </span>
            <span
              className={`filter-chip ${filterActive === 'ativos' ? 'on' : ''}`}
              onClick={() => setFilterActive('ativos')}
              role="button"
            >
              Ativos
            </span>
            <span
              className={`filter-chip ${filterActive === 'inativos' ? 'on' : ''}`}
              onClick={() => setFilterActive('inativos')}
              role="button"
            >
              Inativos
            </span>
            <select
              className="input"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value as Role | '')}
              style={{ maxWidth: 220, marginLeft: 'auto' }}
            >
              <option value="">Todos os papéis</option>
              {COMPANY_ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="empty" style={{ padding: 24 }}>Carregando…</div>
        ) : filtered.length === 0 ? (
          <div className="empty" style={{ padding: 24 }}>Nenhum usuário para os filtros aplicados.</div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Papel</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id}>
                  <td>{u.name}{u.id === me?.id ? ' (você)' : ''}</td>
                  <td>{u.email}</td>
                  <td>
                    <span className={`badge ${ROLE_BADGE[u.role] || 'b-gray'}`}>{ROLE_LABEL[u.role] || u.role}</span>
                  </td>
                  <td>
                    <span className={`badge ${u.active ? 'b-teal' : 'b-gray'}`}>{u.active ? 'Ativo' : 'Inativo'}</span>
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {isSuperAdminContext && u.active && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setConfirmImpersonate(u)}
                        style={{ marginRight: 6 }}
                      >
                        Assumir sessão
                      </button>
                    )}
                    <button className="btn btn-secondary btn-sm" onClick={() => openEdit(u)}>Editar</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => openResetPassword(u)} style={{ marginLeft: 6 }}>
                      Nova senha
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => setConfirmDelete(u)}
                      disabled={u.id === me?.id}
                      style={{ marginLeft: 6 }}
                    >
                      Remover
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar usuário' : 'Convidar usuário'}
        footer={
          <>
            <button className="btn btn-secondary btn-sm" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando…' : editing ? 'Salvar' : 'Convidar'}
            </button>
          </>
        }
      >
        <div className="field">
          <label>Nome</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label>Email {editing && <small style={{ opacity: 0.65 }}>(este é o login do usuário)</small>}</label>
          <input
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label>Papel</label>
          <select className="input" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {COMPANY_ROLES.map((r) => (
              <option key={r} value={r}>{ROLE_LABEL[r]}</option>
            ))}
          </select>
          <small style={{ opacity: 0.75 }}>{ROLE_DESC[role]}</small>
        </div>
        {editing && (
          <div className="field">
            <label>
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              &nbsp;Usuário ativo
            </label>
            <small style={{ opacity: 0.75 }}>
              Desativado não consegue mais fazer login. Não perde os dados vinculados.
            </small>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!inviteResult}
        onClose={() => setInviteResult(null)}
        title="Convite gerado"
        footer={<button className="btn btn-primary" onClick={() => setInviteResult(null)}>Fechar</button>}
      >
        <p>Copie a senha temporária e envie ao usuário pelo canal de sua preferência.</p>
        <div className="field">
          <label>Email</label>
          <input className="input" readOnly value={inviteResult?.email || ''} />
        </div>
        <div className="field">
          <label>Senha temporária</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="input" readOnly value={inviteResult?.tempPassword || ''} />
            <button className="btn btn-secondary btn-sm" onClick={() => inviteResult && copy(inviteResult.tempPassword)}>Copiar</button>
          </div>
        </div>
        <div className="field">
          <label>Mensagem sugerida</label>
          <textarea className="input" rows={4} readOnly value={inviteResult?.inviteMessage || ''} />
          <button
            className="btn btn-secondary btn-sm"
            style={{ marginTop: 6 }}
            onClick={() => inviteResult && copy(inviteResult.inviteMessage)}
          >
            Copiar mensagem
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={!!resetTarget}
        onClose={closeResetDialog}
        title="Nova senha"
        footer={
          <>
            <button className="btn btn-secondary btn-sm" onClick={closeResetDialog} disabled={resetSubmitting}>
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={submitResetPassword} disabled={resetSubmitting}>
              {resetSubmitting
                ? 'Aplicando…'
                : resetMode === 'random'
                  ? 'Gerar senha temporária'
                  : 'Definir senha'}
            </button>
          </>
        }
      >
        <p>
          Redefinir a senha de <strong>{resetTarget?.email}</strong>. A senha atual será invalidada imediatamente.
        </p>
        {isSuperAdminContext && (
          <div className="field" style={{ marginTop: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, textTransform: 'none' }}>
              <input
                type="radio"
                name="reset-mode"
                checked={resetMode === 'random'}
                onChange={() => setResetMode('random')}
              />
              Gerar senha temporária aleatória
              <small style={{ opacity: 0.75, fontWeight: 400 }}>(padrão · usuário precisa trocar no 1º login)</small>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, textTransform: 'none', marginTop: 6 }}>
              <input
                type="radio"
                name="reset-mode"
                checked={resetMode === 'manual'}
                onChange={() => setResetMode('manual')}
              />
              Definir uma senha específica
              <small style={{ opacity: 0.75, fontWeight: 400 }}>(o usuário mantém essa senha)</small>
            </label>
          </div>
        )}

        {resetMode === 'manual' && isSuperAdminContext && (
          <div className="field" style={{ marginTop: 12 }}>
            <label>Senha (mínimo 8 caracteres)</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                className="input"
                type={resetShowPw ? 'text' : 'password'}
                value={resetManualPassword}
                onChange={(e) => setResetManualPassword(e.target.value)}
                autoComplete="new-password"
                autoFocus
              />
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setResetShowPw((s) => !s)}
              >
                {resetShowPw ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
            <small style={{ opacity: 0.75 }}>
              A senha ficará ativa imediatamente. Passe ao usuário pelo canal que você já usa.
            </small>
          </div>
        )}

        {resetError && (
          <p className="msg-error" style={{ marginTop: 10 }}>{resetError}</p>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Remover usuário"
        danger
        message={
          <>
            Remover <strong>{confirmDelete?.name}</strong> ({confirmDelete?.email}) definitivamente?
            Esta ação não pode ser desfeita.
          </>
        }
        confirmLabel="Remover"
        onConfirm={async () => { if (confirmDelete) await doDelete(confirmDelete); }}
      />

      <ConfirmDialog
        isOpen={!!confirmImpersonate}
        onClose={() => setConfirmImpersonate(null)}
        title="Assumir sessão"
        message={
          <>
            Assumir a sessão de <strong>{confirmImpersonate?.email}</strong>?
            Sua ação ficará marcada como <em>suporte</em>, com um banner permanente até você voltar.
          </>
        }
        confirmLabel="Assumir"
        onConfirm={async () => { if (confirmImpersonate) await doImpersonate(confirmImpersonate); }}
      />

      <Modal
        isOpen={!!resetResult}
        onClose={() => setResetResult(null)}
        title="Nova senha temporária"
        footer={<button className="btn btn-primary" onClick={() => setResetResult(null)}>Fechar</button>}
      >
        <p>Envie ao usuário. Ele deve trocar no primeiro login.</p>
        <div className="field">
          <label>Email</label>
          <input className="input" readOnly value={resetResult?.email || ''} />
        </div>
        <div className="field">
          <label>Senha temporária</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="input" readOnly value={resetResult?.tempPassword || ''} />
            <button className="btn btn-secondary btn-sm" onClick={() => resetResult?.tempPassword && copy(resetResult.tempPassword)}>Copiar</button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
}
